import { useTranslation } from 'react-i18next'

import { BULK_REJECT_MAX_ROWS } from './parseBulkRejectCsv'
import { ActionModal, Alert, Spacer } from '@percent/lemonade'

type BulkRejectConfirmViewProps = {
  rowCount: number
  canSubmit: boolean
  hasMissingHeaders: boolean
  missingHeaders: string[]
  duplicateCount: number
  isSubmitting: boolean
  onClose: () => void
  onSubmit: () => void
}

export function BulkRejectConfirmView({
  rowCount,
  canSubmit,
  hasMissingHeaders,
  missingHeaders,
  duplicateCount,
  isSubmitting,
  onClose,
  onSubmit
}: Readonly<BulkRejectConfirmViewProps>) {
  const { t } = useTranslation()

  const renderBlocker = () => {
    if (hasMissingHeaders) {
      return (
        <Alert variant="error" title={t('dialog.avBulkReject.confirm.missingHeaders.title')}>
          {t('dialog.avBulkReject.confirm.missingHeaders.description', { headers: missingHeaders.join(', ') })}
        </Alert>
      )
    }

    if (rowCount === 0) {
      return (
        <Alert variant="error" title={t('dialog.avBulkReject.confirm.empty.title')}>
          {t('dialog.avBulkReject.confirm.empty.description')}
        </Alert>
      )
    }

    return (
      <Alert variant="error" title={t('dialog.avBulkReject.confirm.tooManyRows.title')}>
        {t('dialog.avBulkReject.confirm.tooManyRows.description', { count: rowCount, maxRows: BULK_REJECT_MAX_ROWS })}
      </Alert>
    )
  }

  return (
    <ActionModal
      title={t('dialog.avBulkReject.confirm.title')}
      primaryButtonText={t('dialog.avBulkReject.confirm.submit', { count: rowCount })}
      primaryBtnTestId="av-bulk-reject-submit-button"
      secondaryButtonText={t('button.cancel')}
      handleClose={onClose}
      handleSubmit={onSubmit}
      loading={isSubmitting}
      disabled={!canSubmit || isSubmitting}
    >
      {canSubmit ? (
        <Alert variant="warning" title={t('dialog.avBulkReject.confirm.warning.title')}>
          {t('dialog.avBulkReject.confirm.warning.description', { count: rowCount })}
        </Alert>
      ) : (
        renderBlocker()
      )}
      {duplicateCount > 0 && (
        <>
          <Spacer size={2} />
          <Alert variant="info" title={t('dialog.avBulkReject.confirm.duplicates.title')}>
            {t('dialog.avBulkReject.confirm.duplicates.description', { count: duplicateCount })}
          </Alert>
        </>
      )}
    </ActionModal>
  )
}
